import React, { useEffect, useRef, useState } from 'react';
import {
  POLL_CARD_GAP,
  PLUS_R,
  CIRCLE_R,
  C,
  ny,
  imgUrl,
  normalizeImgField,
  TRIVIA_NODE_W,
  resolveFeatureIconAndLabel,
} from '../flowCanvas.constants';

const BORDER_R = 8;
const CARD_PAD = 10;
const COLLAPSED_H = 58;
const TEXT_LINE_H = 16;
const LEFT_BRANCH_GAP = 12;
const RIGHT_EXIT_MARGIN = 18;
const MAX_ANSWERS = 10;
const ROUND_HEADER_H = 44;
const ROUND_SECTION_GAP = 20;
const ROUND_CONTENT_PAD = 10;
const TOGGLE_H = 20;
const TOGGLE_GAP = 6;
const QUESTION_IMAGE_H = 104;
const ANSWER_IMAGE_H = 78;
const EMPTY_STATE_H = 24;
const ADD_Q_BTN_H = 34;
const CONTENT_TOP_PAD = 12;
const CONTENT_BOTTOM_PAD = 12;
const TEXTAREA_MIN_H = 72;
const ANSWER_INPUT_H = 30;
const ADD_ANSWER_BTN_H = 26;
const GROUP_BOX_PAD = 10;
const ANSWER_TOP_DECOR_H = 18;
const ANSWER_DELETE_R = 9;
const QUESTION_TRIGGER_SLOT_H = 64;

const TRIVIA_CARD_W = TRIVIA_NODE_W;

function ToggleBtn({ x, y, w, h, active, activeColor, label, onClick }) {
  const parts = String(label || '').split(' ');
  const icon = parts[0];
  const text = parts.slice(1).join(' ');

  return (
    <g onClick={onClick} style={{ cursor: 'pointer' }}>
      <rect
        x={x}
        y={y}
        width={w}
        height={h}
        rx="3"
        fill={C.circle}
        stroke={active ? activeColor : C.border}
        strokeWidth="1"
      />
      <circle
        cx={x + 10}
        cy={y + h / 2}
        r="5.5"
        fill={active ? C.tintSuccess : C.circle}
        stroke={active ? activeColor : C.border}
        strokeWidth="0.8"
      />
      <text
        x={x + 10}
        y={y + h / 2 + 3}
        textAnchor="middle"
        fontSize="7.5"
        fontWeight="700"
        fill={active ? activeColor : C.textDim}
        style={{ userSelect: 'none', pointerEvents: 'none' }}
      >
        {icon}
      </text>
      <text
        x={x + 20}
        y={y + h / 2 + 3}
        textAnchor="start"
        fontSize="7.5"
        fontWeight="600"
        fill={active ? activeColor : C.textMuted}
        style={{ userSelect: 'none', pointerEvents: 'none' }}
      >
        {text}
      </text>
    </g>
  );
}

function AnswerBadge({ index }) {
  return (
    <div style={{
      width: '20px',
      height: '20px',
      borderRadius: '999px',
      border: '1px solid rgba(145,70,255,0.45)',
      background: 'rgba(145,70,255,0.18)',
      color: 'var(--fc-text)',
      fontSize: '10px',
      fontWeight: 700,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      userSelect: 'none',
    }}>
      {index}
    </div>
  );
}

function TrashCanGlyph() {
  return (
    <div style={{ position: 'relative', width: '11px', height: '11px', pointerEvents: 'none' }}>
      <div style={{ position: 'absolute', left: '3px', top: '1px', width: '5px', height: '1.8px', borderRadius: '1px', background: '#fee2e2' }} />
      <div style={{ position: 'absolute', left: '1px', top: '3px', width: '9px', height: '1.6px', borderRadius: '1px', background: '#fee2e2' }} />
      <div style={{ position: 'absolute', left: '2px', top: '4px', width: '7px', height: '6px', borderRadius: '1px', background: '#fee2e2' }} />
      <div style={{ position: 'absolute', left: '4px', top: '5px', width: '1px', height: '4px', borderRadius: '1px', background: '#dc2626' }} />
      <div style={{ position: 'absolute', left: '6px', top: '5px', width: '1px', height: '4px', borderRadius: '1px', background: '#dc2626' }} />
    </div>
  );
}

function getAnswers(question) {
  const answers = [];
  for (let index = 1; index <= MAX_ANSWERS; index += 1) {
    const answerText = question?.[`answer${index}`];
    if (answerText === null || answerText === undefined || String(answerText).trim() === '') continue;
    answers.push({
      index,
      key: `answer${index}`,
      text: String(answerText),
      imageField: `answerImage${index}`,
      image: question?.[`answerImage${index}`] ?? null,
    });
  }
  return answers;
}

function resolveImageSrc(field, assetFolder, campaignId) {
  const norm = normalizeImgField(field);
  if (norm.srcUrl) return norm.srcUrl;
  if (!norm.name) return null;
  return imgUrl(norm.name, norm.isDefault, assetFolder, campaignId);
}

function getQuestionSummary(question, answers, assetFolder, campaignId) {
  const summaryBits = [];
  const hasQuestionImage = !!question?.includeQuestionImage && !!resolveImageSrc(question?.questionImage, assetFolder, campaignId);
  const hasAnswerImages = !!question?.includeAnswerImages && answers.some((answer) => !!resolveImageSrc(answer.image, assetFolder, campaignId));

  if (hasQuestionImage) summaryBits.push('Question image');
  if (hasAnswerImages) summaryBits.push('Answer images');
  if (question?.hideQuestionText) summaryBits.push('Question text off');
  if (question?.hideAnswerText) summaryBits.push('Answer text off');

  return summaryBits.length > 0
    ? summaryBits.join(' • ')
    : `${answers.length} answer${answers.length === 1 ? '' : 's'}`;
}

function getQuestionMetaSummary(question, answers, assetFolder, campaignId) {
  const summaryBits = [];
  const hasQuestionImage = !!question?.includeQuestionImage && !!resolveImageSrc(question?.questionImage, assetFolder, campaignId);
  const hasAnswerImages = !!question?.includeAnswerImages && answers.some((answer) => !!resolveImageSrc(answer.image, assetFolder, campaignId));

  if (hasQuestionImage) summaryBits.push('Question image');
  if (hasAnswerImages) summaryBits.push('Answer images');
  if (question?.hideQuestionText) summaryBits.push('Question text off');
  if (question?.hideAnswerText) summaryBits.push('Answer text off');

  return summaryBits.join(' • ');
}

function countTextLines(text, charsPerLine) {
  return Math.max(Math.ceil(String(text || '').trim().length / charsPerLine), 1);
}

export default function TriviaCardsNode({
  step,
  stepX,
  centreY,
  overrideY,
  isSelected,
  activeTriviaIndex,
  liveCustomisationData,
  parentStepX,
  parentStepY,
  nextStepX,
  nextStepY,
  nextStepHalfW,
  onHeightChange,
  onTriviaClick,
  onTriviaFieldActivate,
  onAddTriviaClick,
  onRemoveTrivia,
  onAddRoundClick,
  onRoundClick,
  onQuestionFieldChange,
  onQuestionDisruptorDrop,
  audioSiblingRightX,
  audioSiblingMidY,
  assetFolder,
  campaignId,
  selectedFieldDataLink,
  campaignFeatures,
}) {
  const stepY = overrideY ?? ny(step, centreY);
  const roundsCount = liveCustomisationData?.setup?.triviaData?.triviaSetup?.[0]?.roundsCount ?? 1;
  const rounds = [];

  for (let roundIndex = 1; roundIndex <= roundsCount; roundIndex += 1) {
    const roundVarKey = `triviaData${roundIndex}`;
    const roundData = liveCustomisationData?.setup?.[roundVarKey];
    rounds.push({
      roundVarKey,
      roundName: roundData?.roundSetup?.[0]?.roundName || `Round ${roundIndex}`,
      questions: Array.isArray(roundData?.questions) ? roundData.questions : [],
    });
  }

  const [expandedRounds, setExpandedRounds] = useState(() => {
    const initial = {};
    for (let index = 0; index < roundsCount; index += 1) initial[index] = true;
    return initial;
  });
  const [expandedCards, setExpandedCards] = useState({});
  const [dragOverTriggerKey, setDragOverTriggerKey] = useState(null);
  const onHeightChangeRef = useRef(onHeightChange);

  useEffect(() => {
    onHeightChangeRef.current = onHeightChange;
  }, [onHeightChange]);

  useEffect(() => {
    setExpandedRounds((previous) => {
      const next = { ...previous };
      for (let index = 0; index < roundsCount; index += 1) {
        if (next[index] === undefined) next[index] = true;
      }
      return next;
    });
  }, [roundsCount]);

  const toggleRound = (roundIndex, event) => {
    event.stopPropagation();
    setExpandedRounds((previous) => ({ ...previous, [roundIndex]: !previous[roundIndex] }));
  };

  const toggleCard = (roundVarKey, questionIdx, event) => {
    event.stopPropagation();
    const key = `${roundVarKey}.${questionIdx}`;
    setExpandedCards((previous) => ({ ...previous, [key]: !previous[key] }));
  };

  const handleQuestionFieldToggle = (roundVarKey, questionIdx, field, nextValue, event) => {
    event.stopPropagation();
    onQuestionFieldChange?.(roundVarKey, questionIdx, field, nextValue);
  };

  const handleFieldActivate = (roundVarKey, questionIdx, field, event) => {
    event.preventDefault();
    event.stopPropagation();
    console.log('[TriviaFieldDebug][TriviaCardsNode] handleFieldActivate', {
      stepId: step?.stepId,
      roundVarKey,
      questionIdx,
      field,
    });
    onTriviaFieldActivate?.(roundVarKey, questionIdx, field);
  };

  const handleFieldChange = (roundVarKey, questionIdx, field, value) => {
    onQuestionFieldChange?.(roundVarKey, questionIdx, field, value);
  };

  const interceptInteractivePointerDown = (event) => {
    event.preventDefault();
    event.stopPropagation();
  };

  const stopInteractivePointerDown = (event) => {
    event.stopPropagation();
  };

  const getSelectedFieldFromDataLink = (fieldLink) => {
    if (typeof fieldLink !== 'string' || !fieldLink.length) return null;
    return fieldLink.split('.').pop() || null;
  };

  const isSelectedImageField = (roundVarKey, questionIdx, fieldName) => {
    if (!fieldName) return false;

    if (
      activeTriviaIndex?.roundVarKey === roundVarKey
      && activeTriviaIndex?.questionIdx === questionIdx
      && activeTriviaIndex?.field === fieldName
    ) {
      return true;
    }

    return (
      activeTriviaIndex?.roundVarKey === roundVarKey
      && activeTriviaIndex?.questionIdx === questionIdx
      && getSelectedFieldFromDataLink(selectedFieldDataLink) === fieldName
    );
  };

  const getCardHeight = (question, roundVarKey, questionIdx) => {
    if (!expandedCards[`${roundVarKey}.${questionIdx}`]) return COLLAPSED_H;

    const answers = getAnswers(question);
    const hasQuestionImageSlot = !!question?.includeQuestionImage;
    const hasAnswerImageSlots = !!question?.includeAnswerImages;
    const charsPerLine = Math.max(Math.floor((TRIVIA_CARD_W - ROUND_CONTENT_PAD * 2 - CARD_PAD * 2 - 20) / 7), 20);
    const questionLines = countTextLines(question?.question || '', charsPerLine);
    const questionTextAreaH = Math.max(TEXTAREA_MIN_H, questionLines * TEXT_LINE_H + 26);

    let height = COLLAPSED_H;
    height += CONTENT_TOP_PAD + TOGGLE_H + TOGGLE_GAP + TOGGLE_H + CONTENT_TOP_PAD;
    height += 16 + 8;

    if (hasQuestionImageSlot) {
      height += QUESTION_IMAGE_H + 8;
    }

    height += 16 + 8 + questionTextAreaH + 8;
    height += QUESTION_TRIGGER_SLOT_H;
    height += GROUP_BOX_PAD * 2;
    height += 16 + 8;

    if (answers.length === 0) {
      height += EMPTY_STATE_H;
    } else {
      answers.forEach(() => {
        height += ANSWER_TOP_DECOR_H;
        if (hasAnswerImageSlots) height += ANSWER_IMAGE_H + 8;
        height += ANSWER_INPUT_H + 8;
        height += 16;
      });
      height -= 8;
    }

    if (answers.length < MAX_ANSWERS) {
      height += ADD_ANSWER_BTN_H + 8;
    }

    height += GROUP_BOX_PAD * 2;

    height += CONTENT_BOTTOM_PAD;
    return height;
  };

  const sectionLayouts = [];
  let relCursor = 0;

  for (let roundIndex = 0; roundIndex < rounds.length; roundIndex += 1) {
    const round = rounds[roundIndex];
    const sectionTop = relCursor;
    const questionLayouts = [];
    let addQuestionCenterY = null;
    let emptyStateY = null;

    relCursor += ROUND_HEADER_H;

    if (expandedRounds[roundIndex] !== false) {
      relCursor += ROUND_CONTENT_PAD;

      if (round.questions.length > 0) {
        for (let questionIdx = 0; questionIdx < round.questions.length; questionIdx += 1) {
          const height = getCardHeight(round.questions[questionIdx], round.roundVarKey, questionIdx);
          questionLayouts.push({ relY: relCursor, height });
          relCursor += height;
          if (questionIdx < round.questions.length - 1) relCursor += POLL_CARD_GAP;
        }
      } else {
        emptyStateY = relCursor + 14;
        relCursor += EMPTY_STATE_H;
      }

      relCursor += POLL_CARD_GAP;
      addQuestionCenterY = relCursor + ADD_Q_BTN_H / 2;
      relCursor += ADD_Q_BTN_H;
      relCursor += ROUND_CONTENT_PAD;
    }

    const panelHeight = relCursor - sectionTop;
    sectionLayouts.push({
      sectionTop,
      questionLayouts,
      addQuestionCenterY,
      emptyStateY,
      panelHeight,
    });

    if (roundIndex < rounds.length - 1) relCursor += ROUND_SECTION_GAP;
  }

  const totalHeight = Math.max(relCursor, ROUND_HEADER_H);

  useEffect(() => {
    onHeightChangeRef.current?.(totalHeight);
  }, [totalHeight]);

  useEffect(() => {
    return () => onHeightChangeRef.current?.(null);
  }, []);

  const panelX = stepX - TRIVIA_CARD_W / 2;
  const topY = stepY - totalHeight / 2;
  const cardX = panelX + ROUND_CONTENT_PAD;
  const cardW = TRIVIA_CARD_W - ROUND_CONTENT_PAD * 2;
  const targetLeftX = nextStepX != null ? nextStepX - (nextStepHalfW ?? 58) : null;

  const visibleQuestionPositions = sectionLayouts.flatMap((section) =>
    section.questionLayouts.map((questionLayout) => ({
      midY: topY + questionLayout.relY + questionLayout.height / 2,
    }))
  );

  const connectorGroup = (() => {
    if (parentStepX == null || parentStepY == null) return null;

    const fromX = parentStepX + CIRCLE_R;
    const fromY = parentStepY;
    const spineX = panelX - LEFT_BRANCH_GAP;
    const addX = fromX + (spineX - fromX) / 2;
    const addY = fromY;

    if (visibleQuestionPositions.length === 0) {
      return (
        <g>
          {onAddRoundClick ? (
            <>
              <path d={`M ${fromX} ${fromY} H ${addX - PLUS_R}`} stroke={C.purple} strokeWidth={1.5} fill="none" strokeOpacity={0.82} />
              <text
                x={addX}
                y={addY - PLUS_R - 8}
                textAnchor="middle"
                fontSize="9.5"
                fontWeight="600"
                fill={C.greenText}
                style={{ userSelect: 'none', pointerEvents: 'none' }}
              >
                Add Round
              </text>
              <g
                onMouseDown={interceptInteractivePointerDown}
                onPointerDown={interceptInteractivePointerDown}
                onClick={(event) => { event.stopPropagation(); onAddRoundClick(); }}
                style={{ cursor: 'pointer' }}
              >
                <circle cx={addX} cy={addY} r={PLUS_R + 7} fill="transparent" />
                <circle cx={addX} cy={addY} r={PLUS_R} fill={C.greenText} />
                <text
                  x={addX}
                  y={addY + 1}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize="16"
                  fontWeight="300"
                  fill="#fff"
                  style={{ pointerEvents: 'none', userSelect: 'none' }}
                >
                  +
                </text>
              </g>
              <path d={`M ${addX + PLUS_R} ${fromY} H ${spineX}`} stroke={C.purple} strokeWidth={1.5} fill="none" strokeOpacity={0.82} />
            </>
          ) : (
            <path d={`M ${fromX} ${fromY} H ${spineX}`} stroke={C.purple} strokeWidth={1.5} fill="none" strokeOpacity={0.82} />
          )}
        </g>
      );
    }

    const topBranchY = visibleQuestionPositions[0].midY;
    const bottomBranchY = visibleQuestionPositions[visibleQuestionPositions.length - 1].midY;
    const spineTopY = Math.min(fromY, topBranchY);

    return (
      <g>
        {onAddRoundClick ? (
          <>
            <path d={`M ${fromX} ${fromY} H ${addX - PLUS_R}`} stroke={C.purple} strokeWidth={1.5} fill="none" strokeOpacity={0.82} />
            <text
              x={addX}
              y={addY - PLUS_R - 8}
              textAnchor="middle"
              fontSize="9.5"
              fontWeight="600"
              fill={C.greenText}
              style={{ userSelect: 'none', pointerEvents: 'none' }}
            >
              Add Round
            </text>
            <g
              onMouseDown={interceptInteractivePointerDown}
              onPointerDown={interceptInteractivePointerDown}
              onClick={(event) => { event.stopPropagation(); onAddRoundClick(); }}
              style={{ cursor: 'pointer' }}
            >
              <circle cx={addX} cy={addY} r={PLUS_R + 7} fill="transparent" />
              <circle cx={addX} cy={addY} r={PLUS_R} fill={C.greenText} />
              <text
                x={addX}
                y={addY + 1}
                textAnchor="middle"
                dominantBaseline="middle"
                fontSize="16"
                fontWeight="300"
                fill="#fff"
                style={{ pointerEvents: 'none', userSelect: 'none' }}
              >
                +
              </text>
            </g>
            <path d={`M ${addX + PLUS_R} ${fromY} H ${spineX}`} stroke={C.purple} strokeWidth={1.5} fill="none" strokeOpacity={0.82} />
          </>
        ) : (
          <path d={`M ${fromX} ${fromY} H ${spineX}`} stroke={C.purple} strokeWidth={1.5} fill="none" strokeOpacity={0.82} />
        )}
        <path d={`M ${spineX} ${spineTopY} V ${bottomBranchY}`} stroke={C.purple} strokeWidth={1.5} fill="none" strokeOpacity={0.82} />
        {visibleQuestionPositions.map((position, index) => (
          <path
            key={`trivia-left-${index}`}
            d={`M ${spineX} ${position.midY} H ${cardX}`}
            stroke={C.purple}
            strokeWidth={1.5}
            fill="none"
            strokeOpacity={0.82}
          />
        ))}
      </g>
    );
  })();

  const outgoingConnectorGroup = (() => {
    if (visibleQuestionPositions.length === 0 || nextStepX == null || nextStepY == null) return null;

    const exitStemX = panelX + TRIVIA_CARD_W + RIGHT_EXIT_MARGIN;
    const topBranchY = visibleQuestionPositions[0].midY;
    const bottomBranchY = visibleQuestionPositions[visibleQuestionPositions.length - 1].midY;
    const stemTopY = audioSiblingMidY != null && audioSiblingMidY < topBranchY ? audioSiblingMidY : topBranchY;
    const exitY = nextStepY ?? parentStepY ?? (stemTopY + bottomBranchY) / 2;
    const stemStartY = Math.min(stemTopY, exitY);
    const stemEndY = Math.max(bottomBranchY, exitY);

    return (
      <g>
        {audioSiblingRightX != null && audioSiblingMidY != null && (
          <path d={`M ${audioSiblingRightX} ${audioSiblingMidY} H ${exitStemX}`} stroke={C.purple} strokeWidth={1.5} fill="none" strokeOpacity={0.82} />
        )}
        {visibleQuestionPositions.map((position, index) => (
          <path
            key={`trivia-right-${index}`}
            d={`M ${cardX + cardW} ${position.midY} H ${exitStemX}`}
            stroke={C.purple}
            strokeWidth={1.5}
            fill="none"
            strokeOpacity={0.82}
          />
        ))}
        <path d={`M ${exitStemX} ${stemStartY} V ${stemEndY}`} stroke={C.purple} strokeWidth={1.5} fill="none" strokeOpacity={0.82} />
        <path d={`M ${exitStemX} ${exitY} H ${targetLeftX}`} stroke={C.purple} strokeWidth={1.5} fill="none" strokeOpacity={0.82} />
      </g>
    );
  })();

  const sectionElements = rounds.map((round, roundIndex) => {
    const layout = sectionLayouts[roundIndex];
    const roundExpanded = expandedRounds[roundIndex] !== false;
    const sectionTop = topY + layout.sectionTop;
    const chevronX = panelX + TRIVIA_CARD_W - 18;
    const chevronY = sectionTop + ROUND_HEADER_H / 2;
    const chevronPath = roundExpanded
      ? `M ${chevronX - 5} ${chevronY + 3} L ${chevronX} ${chevronY - 3} L ${chevronX + 5} ${chevronY + 3}`
      : `M ${chevronX - 5} ${chevronY - 3} L ${chevronX} ${chevronY + 3} L ${chevronX + 5} ${chevronY - 3}`;

    const addQuestionButton = roundExpanded && layout.addQuestionCenterY != null ? (() => {
      const btnY = topY + layout.addQuestionCenterY - ADD_Q_BTN_H / 2;

      return (
        <g
          style={{ cursor: 'pointer' }}
          onMouseDown={interceptInteractivePointerDown}
          onPointerDown={interceptInteractivePointerDown}
          onClick={(event) => { event.stopPropagation(); onAddTriviaClick?.(round.roundVarKey); }}
        >
          <rect
            x={cardX}
            y={btnY}
            width={cardW}
            height={ADD_Q_BTN_H}
            rx={6}
            ry={6}
            fill="transparent"
            stroke={`${C.greenText}88`}
            strokeWidth={1}
            strokeDasharray="4 3"
          />
          <text
            x={cardX + cardW / 2}
            y={btnY + ADD_Q_BTN_H / 2 + 4}
            fontSize="9.5"
            textAnchor="middle"
            fill={C.greenText}
            style={{ userSelect: 'none', pointerEvents: 'none' }}
          >
            ＋ Add Question
          </text>
        </g>
      );
    })() : null;

    return (
      <g key={`round-${round.roundVarKey}`}>
        <rect
          x={panelX}
          y={sectionTop}
          width={TRIVIA_CARD_W}
          height={layout.panelHeight}
          rx={BORDER_R}
          fill="rgba(145,70,255,0.05)"
          stroke={isSelected ? C.sel : C.purple}
          strokeWidth={1.5}
          strokeOpacity={0.55}
          style={{ pointerEvents: 'none' }}
        />
        <clipPath id={`trivia-round-header-${step.stepId}-${roundIndex}`}>
          <rect x={panelX} y={sectionTop} width={TRIVIA_CARD_W} height={ROUND_HEADER_H} rx={BORDER_R} />
        </clipPath>
        <rect
          x={panelX}
          y={sectionTop}
          width={TRIVIA_CARD_W}
          height={ROUND_HEADER_H + 16}
          rx={BORDER_R}
          clipPath={`url(#trivia-round-header-${step.stepId}-${roundIndex})`}
          fill="rgba(145,70,255,0.16)"
          style={{ pointerEvents: 'none' }}
        />
        <rect
          x={panelX + 10}
          y={sectionTop + 6}
          width={TRIVIA_CARD_W - 44}
          height={ROUND_HEADER_H - 12}
          rx={6}
          fill="transparent"
          onClick={(event) => {
            event.stopPropagation();
            onRoundClick?.(round.roundVarKey);
          }}
          style={{ cursor: 'pointer' }}
        />
        <text
          x={panelX + 14}
          y={sectionTop + 19}
          fontSize="8.5"
          fontWeight="700"
          fill={C.textMuted}
          style={{ userSelect: 'none', pointerEvents: 'none' }}
        >
          ROUND SETUP
        </text>
        <text
          x={panelX + 14}
          y={sectionTop + 34}
          fontSize="11.5"
          fontWeight="700"
          fill={C.text}
          style={{ userSelect: 'none', pointerEvents: 'none' }}
        >
          {round.roundName.length > 34 ? `${round.roundName.slice(0, 32)}…` : round.roundName}
        </text>
        <text
          x={panelX + TRIVIA_CARD_W - 44}
          y={sectionTop + 34}
          textAnchor="end"
          fontSize="9"
          fontWeight="600"
          fill={C.textMuted}
          style={{ userSelect: 'none', pointerEvents: 'none' }}
        >
          {`${round.questions.length} question${round.questions.length === 1 ? '' : 's'}`}
        </text>
        <path
          d={chevronPath}
          fill="none"
          stroke={C.textMuted}
          strokeWidth={1.5}
          strokeLinecap="round"
          strokeLinejoin="round"
          style={{ cursor: 'pointer' }}
          onClick={(event) => toggleRound(roundIndex, event)}
        />
        <rect
          x={chevronX - 12}
          y={chevronY - 12}
          width={24}
          height={24}
          fill="transparent"
          style={{ cursor: 'pointer' }}
          onClick={(event) => toggleRound(roundIndex, event)}
        />

        {roundExpanded && layout.emptyStateY != null && round.questions.length === 0 && (
          <text
            x={panelX + TRIVIA_CARD_W / 2}
            y={topY + layout.emptyStateY}
            textAnchor="middle"
            fontSize="9"
            fill={C.textDim}
            style={{ userSelect: 'none', pointerEvents: 'none' }}
          >
            No questions in this round yet
          </text>
        )}

        {roundExpanded && round.questions.map((question, questionIdx) => {
          const questionLayout = layout.questionLayouts[questionIdx];
          if (!questionLayout) return null;

          const questionTop = topY + questionLayout.relY;
          const cardHeight = questionLayout.height;
          const isActiveQuestion = activeTriviaIndex?.roundVarKey === round.roundVarKey && activeTriviaIndex?.questionIdx === questionIdx;
          const isExpanded = !!expandedCards[`${round.roundVarKey}.${questionIdx}`];
          const chevronCardX = cardX + cardW - 24;
          const chevronCardY = questionTop + COLLAPSED_H / 2;
          const deleteX = cardX + cardW - 54;
          const deleteY = questionTop + COLLAPSED_H / 2;
          const rowMidY = questionTop + COLLAPSED_H / 2;
          const indexChipX = cardX + CARD_PAD;
          const titleX = cardX + 58;
          const answerBadgeX = cardX + cardW - 142;
          const textRightX = answerBadgeX - 12;
          const title = question?.title || `Question ${questionIdx + 1}`;
          const answers = getAnswers(question);
          const answerCountLabel = `${answers.length} answer${answers.length === 1 ? '' : 's'}`;
          const summary = getQuestionMetaSummary(question, answers, assetFolder, campaignId);
          const titleMaxChars = Math.max(Math.floor((textRightX - titleX) / 6.35), 18);
          const summaryMaxChars = Math.max(Math.floor((textRightX - titleX) / 6.8), 14);
          const questionText = question?.question || '';
          const questionImageSrc = question?.includeQuestionImage
            ? resolveImageSrc(question?.questionImage, assetFolder, campaignId)
            : null;
          const isQuestionImageSelected = isSelectedImageField(round.roundVarKey, questionIdx, 'questionImage');
          const contentTopY = questionTop + COLLAPSED_H + CONTENT_TOP_PAD + TOGGLE_H + TOGGLE_GAP + TOGGLE_H + CONTENT_TOP_PAD;
          const contentHeight = cardHeight - (contentTopY - questionTop) - CONTENT_BOTTOM_PAD;
          const toggleW = (cardW - CARD_PAD * 2 - TOGGLE_GAP) / 2;
          const chevronCardPath = isExpanded
            ? `M ${chevronCardX - 5} ${chevronCardY + 3} L ${chevronCardX} ${chevronCardY - 3} L ${chevronCardX + 5} ${chevronCardY + 3}`
            : `M ${chevronCardX - 5} ${chevronCardY - 3} L ${chevronCardX} ${chevronCardY + 3} L ${chevronCardX + 5} ${chevronCardY - 3}`;
          const cardSelectHitHeight = isExpanded ? COLLAPSED_H : cardHeight;

          return (
            <g key={`${round.roundVarKey}-${questionIdx}`}>
              <rect
                x={cardX}
                y={questionTop}
                width={cardW}
                height={cardHeight}
                rx={BORDER_R}
                fill={C.card}
                stroke={isActiveQuestion ? C.greenText : isSelected ? C.sel : C.border}
                strokeWidth={isActiveQuestion ? 2 : 1.5}
                style={{ pointerEvents: 'none' }}
              />
              <rect
                x={cardX}
                y={questionTop}
                width={cardW}
                height={cardSelectHitHeight}
                rx={BORDER_R}
                fill="transparent"
                onClick={(event) => {
                  event.stopPropagation();
                  onTriviaClick?.(round.roundVarKey, questionIdx);
                }}
                style={{ cursor: 'pointer' }}
              />
              <rect
                x={indexChipX}
                y={questionTop + 12}
                width={34}
                height={COLLAPSED_H - 24}
                rx={7}
                fill={C.circle}
                stroke={C.border}
                strokeWidth={1}
                style={{ pointerEvents: 'none' }}
              />
              <text
                x={indexChipX + 17}
                y={rowMidY + 3.5}
                fontSize="10"
                fontWeight="700"
                fill={C.textMuted}
                textAnchor="middle"
                style={{ userSelect: 'none', pointerEvents: 'none' }}
              >
                Q{questionIdx + 1}
              </text>
              <text
                x={titleX}
                y={questionTop + 25}
                fontSize="11"
                fontWeight="700"
                fill={C.text}
                style={{ userSelect: 'none', pointerEvents: 'none' }}
              >
                {title.length > titleMaxChars ? `${title.slice(0, Math.max(titleMaxChars - 2, 1))}…` : title}
              </text>
              {summary ? (
                <text
                  x={titleX}
                  y={questionTop + 40}
                  fontSize="9"
                  fill={C.textMuted}
                  style={{ userSelect: 'none', pointerEvents: 'none' }}
                >
                  {summary.length > summaryMaxChars ? `${summary.slice(0, Math.max(summaryMaxChars - 2, 1))}…` : summary}
                </text>
              ) : null}
              <rect
                x={answerBadgeX}
                y={questionTop + 14}
                width={74}
                height={COLLAPSED_H - 28}
                rx={10}
                fill="rgba(74,222,128,0.12)"
                stroke="rgba(74,222,128,0.38)"
                strokeWidth={1}
                style={{ pointerEvents: 'none' }}
              />
              <text
                x={answerBadgeX + 37}
                y={rowMidY + 3.5}
                fontSize="9"
                fontWeight="700"
                fill={C.greenText}
                textAnchor="middle"
                style={{ userSelect: 'none', pointerEvents: 'none' }}
              >
                {answerCountLabel}
              </text>
              <path
                d={chevronCardPath}
                fill="none"
                stroke={C.textMuted}
                strokeWidth={1.5}
                strokeLinecap="round"
                strokeLinejoin="round"
                style={{ cursor: 'pointer' }}
                onClick={(event) => toggleCard(round.roundVarKey, questionIdx, event)}
              />
              <rect
                x={chevronCardX - 14}
                y={chevronCardY - 12}
                width={28}
                height={24}
                fill="transparent"
                style={{ cursor: 'pointer' }}
                onClick={(event) => toggleCard(round.roundVarKey, questionIdx, event)}
              />

              {onRemoveTrivia && (
                <g
                  style={{ cursor: 'pointer' }}
                  onClick={(event) => {
                    event.stopPropagation();
                    onRemoveTrivia(round.roundVarKey, questionIdx);
                  }}
                >
                  <circle cx={deleteX} cy={deleteY} r={10} fill="#dc2626" />
                  <g transform={`translate(${deleteX}, ${deleteY})`} style={{ pointerEvents: 'none', userSelect: 'none' }}>
                    <rect x="-2.5" y="-6" width="5" height="2.5" rx="1.2" fill="#fee2e2" />
                    <rect x="-6" y="-4" width="12" height="2" rx="1" fill="#fee2e2" />
                    <rect x="-4.5" y="-1.5" width="9" height="8" rx="1" fill="#fee2e2" />
                    <rect x="-2" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
                    <rect x="0.8" y="0" width="1.2" height="5" rx="0.6" fill="#dc2626" />
                  </g>
                </g>
              )}

              {isExpanded && (
                <g>
                  <line
                    x1={cardX + 8}
                    y1={questionTop + COLLAPSED_H}
                    x2={cardX + cardW - 8}
                    y2={questionTop + COLLAPSED_H}
                    stroke={C.border}
                    strokeWidth={1}
                    style={{ pointerEvents: 'none' }}
                  />
                  <ToggleBtn
                    x={cardX + CARD_PAD}
                    y={questionTop + COLLAPSED_H + CONTENT_TOP_PAD}
                    w={toggleW}
                    h={TOGGLE_H}
                    active={!!question?.includeQuestionImage}
                    activeColor={C.purple}
                    label="I Question Image"
                    onClick={(event) => handleQuestionFieldToggle(round.roundVarKey, questionIdx, 'includeQuestionImage', !question?.includeQuestionImage, event)}
                  />
                  <ToggleBtn
                    x={cardX + CARD_PAD + toggleW + TOGGLE_GAP}
                    y={questionTop + COLLAPSED_H + CONTENT_TOP_PAD}
                    w={toggleW}
                    h={TOGGLE_H}
                    active={!question?.hideQuestionText}
                    activeColor={C.purple}
                    label="T Question Text"
                    onClick={(event) => handleQuestionFieldToggle(round.roundVarKey, questionIdx, 'hideQuestionText', !question?.hideQuestionText, event)}
                  />
                  <ToggleBtn
                    x={cardX + CARD_PAD}
                    y={questionTop + COLLAPSED_H + CONTENT_TOP_PAD + TOGGLE_H + TOGGLE_GAP}
                    w={toggleW}
                    h={TOGGLE_H}
                    active={!!question?.includeAnswerImages}
                    activeColor={C.purple}
                    label="I Answer Images"
                    onClick={(event) => handleQuestionFieldToggle(round.roundVarKey, questionIdx, 'includeAnswerImages', !question?.includeAnswerImages, event)}
                  />
                  <ToggleBtn
                    x={cardX + CARD_PAD + toggleW + TOGGLE_GAP}
                    y={questionTop + COLLAPSED_H + CONTENT_TOP_PAD + TOGGLE_H + TOGGLE_GAP}
                    w={toggleW}
                    h={TOGGLE_H}
                    active={!question?.hideAnswerText}
                    activeColor={C.purple}
                    label="T Answer Text"
                    onClick={(event) => handleQuestionFieldToggle(round.roundVarKey, questionIdx, 'hideAnswerText', !question?.hideAnswerText, event)}
                  />
                  <foreignObject
                    x={cardX + CARD_PAD}
                    y={contentTopY}
                    width={cardW - CARD_PAD * 2}
                    height={contentHeight}
                  >
                    <div
                      xmlns="http://www.w3.org/1999/xhtml"
                      onClick={(event) => event.stopPropagation()}
                      onMouseDown={(event) => event.stopPropagation()}
                      onPointerDown={(event) => event.stopPropagation()}
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '10px',
                        width: '100%',
                        height: '100%',
                        boxSizing: 'border-box',
                        color: 'var(--fc-text)',
                        fontFamily: 'inherit',
                      }}
                    >
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', border: '1px solid var(--fc-border)', borderRadius: '10px', padding: `${GROUP_BOX_PAD}px`, background: 'var(--fc-circle-bg)' }}>
                        <div style={{ fontSize: '9.5px', fontWeight: 700, color: 'var(--fc-text-muted)' }}>QUESTION</div>
                        {!!question?.includeQuestionImage && (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                            <div style={{ fontSize: '9px', fontWeight: 700, color: 'var(--fc-text-dim)' }}>IMAGE</div>
                            <div
                              onClick={(event) => handleFieldActivate(round.roundVarKey, questionIdx, 'questionImage', event)}
                              onMouseDown={stopInteractivePointerDown}
                              onPointerDown={stopInteractivePointerDown}
                              style={{
                                width: '100%',
                                height: `${QUESTION_IMAGE_H}px`,
                                borderRadius: '8px',
                                overflow: 'hidden',
                                border: isQuestionImageSelected ? '2px solid #22c55e' : '1px solid rgba(145,70,255,0.28)',
                                background: 'linear-gradient(45deg, rgba(255,255,255,0.05) 25%, transparent 25%, transparent 75%, rgba(255,255,255,0.05) 75%), linear-gradient(45deg, rgba(255,255,255,0.05) 25%, transparent 25%, transparent 75%, rgba(255,255,255,0.05) 75%)',
                                backgroundSize: '12px 12px',
                                backgroundPosition: '0 0, 6px 6px',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                boxShadow: isQuestionImageSelected ? '0 0 0 2px rgba(34,197,94,0.22), inset 0 0 0 1px rgba(255,255,255,0.08)' : 'none',
                                backgroundColor: isQuestionImageSelected ? 'rgba(34,197,94,0.08)' : 'transparent',
                              }}
                            >
                              {questionImageSrc ? (
                                <img src={questionImageSrc} alt="Question" style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                              ) : (
                                <div style={{ fontSize: '10px', color: 'var(--fc-text-muted)', textAlign: 'center', padding: '0 12px' }}>Click to edit question image</div>
                              )}
                            </div>
                          </div>
                        )}
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <div style={{ fontSize: '9px', fontWeight: 700, color: 'var(--fc-text-dim)' }}>COPY</div>
                          <textarea
                            key={`question-${round.roundVarKey}-${questionIdx}-${questionText}`}
                            defaultValue={questionText}
                            placeholder="Enter question"
                            onBlur={(event) => handleFieldChange(round.roundVarKey, questionIdx, 'question', event.target.value)}
                            style={{
                              minHeight: `${TEXTAREA_MIN_H}px`,
                              borderRadius: '8px',
                              border: '1px solid var(--fc-border)',
                              background: 'var(--fc-circle-bg)',
                              color: 'var(--fc-text)',
                              padding: '10px',
                              fontSize: '10px',
                              outline: 'none',
                              boxSizing: 'border-box',
                              resize: 'vertical',
                              fontFamily: 'inherit',
                              lineHeight: '1.45',
                            }}
                          />
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
                          <div style={{ fontSize: '9px', fontWeight: 700, color: 'var(--fc-text-dim)' }}>QUESTION START TRIGGER</div>
                          {(() => {
                            const triggerKey = `${round.roundVarKey}.${questionIdx}.startQuestionDisruptor`;
                            const triggerId = question?.startQuestionDisruptor || '';
                            const triggerMeta = resolveFeatureIconAndLabel(triggerId, campaignFeatures);
                            const isTriggerDragOver = dragOverTriggerKey === triggerKey;
                            const triggerLabel = triggerId
                              ? (triggerMeta.label.length > 24 ? `${triggerMeta.label.slice(0, 22)}…` : triggerMeta.label)
                              : 'Drag a component here';

                            return (
                              <div
                                onClick={(event) => event.stopPropagation()}
                                onDragEnter={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  setDragOverTriggerKey(triggerKey);
                                }}
                                onDragLeave={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  setDragOverTriggerKey((current) => (current === triggerKey ? null : current));
                                }}
                                onDragOver={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  if (dragOverTriggerKey !== triggerKey) setDragOverTriggerKey(triggerKey);
                                }}
                                onDrop={(event) => {
                                  event.preventDefault();
                                  event.stopPropagation();
                                  setDragOverTriggerKey((current) => (current === triggerKey ? null : current));
                                  onQuestionDisruptorDrop?.(round.roundVarKey, questionIdx, event);
                                }}
                                style={{
                                  minHeight: '42px',
                                  borderRadius: '8px',
                                  border: triggerId ? `1px solid ${C.purple}` : `1px dashed ${isTriggerDragOver ? C.greenText : C.border}`,
                                  background: triggerId ? 'rgba(145,70,255,0.10)' : (isTriggerDragOver ? 'rgba(34,197,94,0.08)' : 'rgba(255,255,255,0.03)'),
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: triggerId ? 'space-between' : 'center',
                                  gap: '8px',
                                  padding: '0 10px',
                                  cursor: 'pointer',
                                }}
                              >
                                {triggerId ? (
                                  <>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', minWidth: 0 }}>
                                      <div style={{ width: '22px', height: '22px', borderRadius: '999px', background: 'rgba(145,70,255,0.16)', border: '1px solid rgba(145,70,255,0.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', flex: '0 0 auto' }}>
                                        <span style={{ fontSize: '12px', lineHeight: 1 }}>{triggerMeta.icon}</span>
                                      </div>
                                      <div style={{ fontSize: '9.5px', fontWeight: 700, color: 'var(--fc-text)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {triggerLabel}
                                      </div>
                                    </div>
                                    <button
                                      type="button"
                                      onMouseDown={interceptInteractivePointerDown}
                                      onPointerDown={interceptInteractivePointerDown}
                                      onClick={(event) => {
                                        event.stopPropagation();
                                        onQuestionFieldChange?.(round.roundVarKey, questionIdx, 'startQuestionDisruptor', '');
                                      }}
                                      style={{
                                        width: `${ANSWER_DELETE_R * 2}px`,
                                        height: `${ANSWER_DELETE_R * 2}px`,
                                        borderRadius: '999px',
                                        border: 'none',
                                        background: '#dc2626',
                                        cursor: 'pointer',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        padding: 0,
                                        flex: '0 0 auto',
                                      }}
                                      aria-label="Remove question start trigger"
                                    >
                                      <TrashCanGlyph />
                                    </button>
                                  </>
                                ) : (
                                  <div style={{ fontSize: '10px', color: isTriggerDragOver ? '#dcfce7' : 'var(--fc-text-dim)', textAlign: 'center' }}>
                                    {triggerLabel}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                        </div>
                      </div>

                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', border: '1px solid var(--fc-border)', borderRadius: '10px', padding: `${GROUP_BOX_PAD}px`, background: 'var(--fc-circle-bg)' }}>
                        <div style={{ fontSize: '9.5px', fontWeight: 700, color: 'var(--fc-text-muted)' }}>ANSWERS</div>
                        {answers.length === 0 && (
                          <div style={{ fontSize: '10px', color: 'var(--fc-text-muted)' }}>No answers configured</div>
                        )}
                        {answers.map((answer) => {
                          const answerImageSrc = question?.includeAnswerImages
                            ? resolveImageSrc(answer.image, assetFolder, campaignId)
                            : null;
                          const isAnswerImageSelected = isSelectedImageField(round.roundVarKey, questionIdx, answer.imageField);

                          return (
                            <div key={answer.key} style={{ display: 'flex', flexDirection: 'column', gap: '8px', position: 'relative', border: '1px solid var(--fc-border)', borderRadius: '8px', padding: `${ANSWER_TOP_DECOR_H}px 8px 8px` }}>
                              <div style={{ position: 'absolute', top: '-10px', left: '4px' }}>
                                <AnswerBadge index={answer.index} />
                              </div>
                              <button
                                type="button"
                                onMouseDown={interceptInteractivePointerDown}
                                onPointerDown={interceptInteractivePointerDown}
                                onClick={(event) => {
                                  event.stopPropagation();
                                  onQuestionFieldChange?.(round.roundVarKey, questionIdx, '_deleteAnswer_', answer.key);
                                }}
                                style={{
                                  position: 'absolute',
                                  top: '-9px',
                                  right: '8px',
                                  width: `${ANSWER_DELETE_R * 2}px`,
                                  height: `${ANSWER_DELETE_R * 2}px`,
                                  borderRadius: '999px',
                                  border: 'none',
                                  background: '#dc2626',
                                  cursor: 'pointer',
                                  display: 'flex',
                                  alignItems: 'center',
                                  justifyContent: 'center',
                                  padding: 0,
                                }}
                                aria-label={`Delete answer ${answer.index}`}
                              >
                                <TrashCanGlyph />
                              </button>
                              {!!question?.includeAnswerImages && (
                                <div
                                  onClick={(event) => handleFieldActivate(round.roundVarKey, questionIdx, answer.imageField, event)}
                                  onMouseDown={stopInteractivePointerDown}
                                  onPointerDown={stopInteractivePointerDown}
                                  style={{
                                    width: '100%',
                                    height: `${ANSWER_IMAGE_H}px`,
                                    borderRadius: '6px',
                                    overflow: 'hidden',
                                    border: isAnswerImageSelected ? '2px solid #22c55e' : '1px solid rgba(145,70,255,0.28)',
                                    background: 'linear-gradient(45deg, rgba(255,255,255,0.05) 25%, transparent 25%, transparent 75%, rgba(255,255,255,0.05) 75%), linear-gradient(45deg, rgba(255,255,255,0.05) 25%, transparent 25%, transparent 75%, rgba(255,255,255,0.05) 75%)',
                                    backgroundSize: '12px 12px',
                                    backgroundPosition: '0 0, 6px 6px',
                                    cursor: 'pointer',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    boxShadow: isAnswerImageSelected ? '0 0 0 2px rgba(34,197,94,0.22), inset 0 0 0 1px rgba(255,255,255,0.08)' : 'none',
                                    backgroundColor: isAnswerImageSelected ? 'rgba(34,197,94,0.08)' : 'transparent',
                                  }}
                                >
                                  {answerImageSrc ? (
                                    <img src={answerImageSrc} alt={answer.text} style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }} />
                                  ) : (
                                    <div style={{ fontSize: '10px', color: 'var(--fc-text-muted)', textAlign: 'center', padding: '0 12px' }}>{`Click to edit image ${answer.index}`}</div>
                                  )}
                                </div>
                              )}
                              <input
                                key={`answer-${round.roundVarKey}-${questionIdx}-${answer.key}-${answer.text}`}
                                defaultValue={answer.text}
                                placeholder={`Answer ${answer.index}`}
                                onBlur={(event) => handleFieldChange(round.roundVarKey, questionIdx, answer.key, event.target.value)}
                                onKeyDown={(event) => { if (event.key === 'Enter') event.currentTarget.blur(); }}
                                style={{
                                  width: '100%',
                                  height: `${ANSWER_INPUT_H}px`,
                                  borderRadius: '8px',
                                  border: '1px solid var(--fc-border)',
                                  background: 'var(--fc-circle-bg)',
                                  color: 'var(--fc-text)',
                                  padding: '0 10px',
                                  fontSize: '9.5px',
                                  outline: 'none',
                                  boxSizing: 'border-box',
                                }}
                              />
                            </div>
                          );
                        })}
                        {answers.length < MAX_ANSWERS && (
                          <button
                            type="button"
                            onMouseDown={interceptInteractivePointerDown}
                            onPointerDown={interceptInteractivePointerDown}
                            onClick={(event) => {
                              event.stopPropagation();
                              onQuestionFieldChange?.(round.roundVarKey, questionIdx, '_addAnswer_', null);
                            }}
                            style={{
                              height: `${ADD_ANSWER_BTN_H}px`,
                              borderRadius: '8px',
                              border: `1px dashed ${C.greenText}88`,
                              background: 'transparent',
                              color: C.greenText,
                              fontSize: '9.5px',
                              cursor: 'pointer',
                            }}
                          >
                            ＋ Add answer
                          </button>
                        )}
                      </div>
                    </div>
                  </foreignObject>
                </g>
              )}
            </g>
          );
        })}

        {addQuestionButton}
      </g>
    );
  });

  return (
    <g className="fc-node fc-node--trivia-cards">
      {connectorGroup}
      {outgoingConnectorGroup}
      {sectionElements}
    </g>
  );
}
